import { FC } from 'react';

import styles from '../LoadingSpinner.module.css';

interface LoadingSpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: number;
  text?: string;
  className?: string;
}

/**
 * LoadingSpinner - Three dots loading animation
 *
 * A refined, minimal loading indicator using three bouncing dots in the
 * brand gold color. Aligns with the Modern Intimate design system.
 *
 * Use for:
 * - Splash screens
 * - Initial page load
 * - Premium/highlighted loading states
 * - Brand-forward contexts
 *
 * For subtle loading indicators, use a simple circular spinner instead.
 *
 * Features:
 * - Three bouncing dots with staggered animation
 * - Configurable size and optional text
 * - Smooth animation with reduced motion support
 *
 * @example
 * <LoadingSpinner size={80} text="Loading your stories..." />
 */
const LoadingSpinner: FC<LoadingSpinnerProps> = ({
  size = 80,
  text = 'Loading',
  className = '',
  ...rest
}) => {
  // Dot size is 1/10th of container, with 6px minimum for readability
  const dotSize = Math.max(6, Math.round(size / 10));
  // Gap is 75% of dot size, with 4px minimum
  const gap = Math.max(4, Math.round(dotSize * 0.75));

  return (
    <div className={`${styles.spinnerWrapper} ${className}`} {...rest}>
      <div
        className={styles.dotsContainer}
        style={{ gap }}
        role="status"
        aria-label={text || 'Loading'}
      >
        <div className={styles.dot} style={{ width: dotSize, height: dotSize }} />
        <div className={styles.dot} style={{ width: dotSize, height: dotSize }} />
        <div className={styles.dot} style={{ width: dotSize, height: dotSize }} />
      </div>
      {text && <div className={styles.loadingText}>{text}</div>}
    </div>
  );
};

export default LoadingSpinner;
